import { useCallback, useEffect, useRef, useState } from 'react';
import { cy } from '../../components/DiagramCanvas';
import { compileSimulationGraph, getInitialNodeValue } from '../engine/graph-compiler';
import { MockExecutionAdapter } from '../adapters/mock-execution-adapter';
import { ensureProcessStepVisible } from '../camera/execution-camera';
import type { DiagramTreeNode } from '../../model/diagram-tree-model';
import type {
  RuntimeToken,
  RuntimeValueBadge,
  SimulationGraph,
  SimulationProcessNode,
  SimulationSnapshot,
  SimulationStatus,
} from '../types';

const INITIAL_SNAPSHOT: SimulationSnapshot = {
  status: 'idle',
  activeProcessId: null,
  runtimeValues: {},
  tokens: [],
};

const INPUT_ANIMATION_DURATION = 480;
const OUTPUT_ANIMATION_DURATION = 520;
const PROCESS_SETTLE_DURATION = 240;
const FRAME_DURATION = 40;

const mockExecutionAdapter = new MockExecutionAdapter();
type SnapshotStateUpdater =
  | SimulationSnapshot
  | ((prev: SimulationSnapshot) => SimulationSnapshot);

interface UseSimulationControllerOptions {
  resetKey?: unknown;
  navigateToDiagram?: (diagram: DiagramTreeNode) => Promise<void>;
}

const toDisplayText = (value: unknown) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const buildRuntimeValue = (
  nodeId: string,
  rawValue: unknown,
  sourceProcessId?: string
): RuntimeValueBadge => ({
  nodeId,
  rawValue,
  displayText: toDisplayText(rawValue),
  sourceProcessId,
  updatedAt: Date.now(),
});

const createTokens = (
  runId: number,
  edges: { id: string; sourceId: string; targetId: string }[],
  phase: 'input' | 'output'
): RuntimeToken[] =>
  edges.map((edge, index) => ({
    id: `${runId}-${phase}-${edge.id}-${index}`,
    edgeId: edge.id,
    sourceId: edge.sourceId,
    targetId: edge.targetId,
    progress: 0,
    phase,
  }));

export const useSimulationController = (
  options: UseSimulationControllerOptions = {}
) => {
  const [snapshot, setSnapshot] = useState<SimulationSnapshot>(INITIAL_SNAPSHOT);
  const runIdRef = useRef(0);
  const timeoutIdsRef = useRef<number[]>([]);
  const { resetKey, navigateToDiagram } = options;
  const lastResetKeyRef = useRef(resetKey);

  const updateSnapshot = useCallback((updater: SnapshotStateUpdater) => {
    setSnapshot(prev => {
      const next =
        typeof updater === 'function'
          ? (updater as (prev: SimulationSnapshot) => SimulationSnapshot)(prev)
          : updater;
      return next;
    });
  }, []);

  const clearScheduledTasks = useCallback(() => {
    timeoutIdsRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
    timeoutIdsRef.current = [];
  }, []);

  const scheduleWait = useCallback(
    (ms: number) =>
      new Promise<void>(resolve => {
        const timeoutId = window.setTimeout(() => {
          timeoutIdsRef.current = timeoutIdsRef.current.filter(id => id !== timeoutId);
          resolve();
        }, ms);
        timeoutIdsRef.current.push(timeoutId);
      }),
    []
  );

  const stopSimulation = useCallback((status: SimulationStatus = 'cancelled') => {
    runIdRef.current += 1;
    clearScheduledTasks();
    updateSnapshot(prev => ({
      ...prev,
      status,
      activeProcessId: null,
      tokens: [],
      finishedAt: Date.now(),
    }));
  }, [clearScheduledTasks, updateSnapshot]);

  const resetSimulation = useCallback(() => {
    runIdRef.current += 1;
    clearScheduledTasks();
    updateSnapshot(INITIAL_SNAPSHOT);
  }, [clearScheduledTasks, updateSnapshot]);

  const animateTokens = useCallback(
    async (
      runId: number,
      edges: { id: string; sourceId: string; targetId: string }[],
      phase: 'input' | 'output',
      duration: number
    ) => {
      if (!edges.length) return;

      const frames = Math.max(1, Math.round(duration / FRAME_DURATION));
      const baseTokens = createTokens(runId, edges, phase);

      for (let frameIndex = 0; frameIndex <= frames; frameIndex += 1) {
        if (runIdRef.current !== runId) return;
        const progress = frameIndex / frames;
        updateSnapshot(prev => ({
          ...prev,
          tokens: baseTokens.map(token => ({
            ...token,
            progress,
          })),
        }));
        await scheduleWait(FRAME_DURATION);
      }

      if (runIdRef.current === runId) {
        updateSnapshot(prev => ({
          ...prev,
          tokens: [],
        }));
      }
    },
    [scheduleWait, updateSnapshot]
  );

  const startSimulation = useCallback(async (startDiagram?: DiagramTreeNode | null) => {
    if (!cy) {
      throw new Error('画布尚未初始化，暂时无法执行仿真');
    }

    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    clearScheduledTasks();

    let currentRuntimeValues: Record<string, RuntimeValueBadge> = {};

    const ensureGraphRuntimeValues = (graph: SimulationGraph) => {
      let hasChanged = false;
      const nextRuntimeValues = { ...currentRuntimeValues };

      graph.inputNodeIds.forEach(nodeId => {
        if (nextRuntimeValues[nodeId]) return;
        const node = graph.nodes[nodeId];
        if (!node) return;
        nextRuntimeValues[nodeId] = buildRuntimeValue(nodeId, getInitialNodeValue(node));
        hasChanged = true;
      });

      if (!hasChanged) return;
      currentRuntimeValues = nextRuntimeValues;
      updateSnapshot(prev => ({
        ...prev,
        runtimeValues: currentRuntimeValues,
      }));
    };

    const executeDiagram = async (diagram: DiagramTreeNode | null) => {
      if (!cy || runIdRef.current !== runId) return;

      if (diagram && navigateToDiagram) {
        await navigateToDiagram(diagram);
        if (runIdRef.current !== runId) return;
      }

      const graph = compileSimulationGraph(cy);
      if (graph.orderedProcessIds.length === 0) {
        return;
      }

      ensureGraphRuntimeValues(graph);

      const executeChildDiagram = async (
        process: SimulationProcessNode,
        currentDiagram: DiagramTreeNode | null
      ) => {
        const childDiagram = process.subDiagram;
        if (!childDiagram || childDiagram === currentDiagram) return;

        updateSnapshot(prev => ({
          ...prev,
          activeProcessId: null,
        }));

        try {
          await executeDiagram(childDiagram);
        } finally {
          if (runIdRef.current === runId && currentDiagram && navigateToDiagram) {
            await navigateToDiagram(currentDiagram);
          }
        }

        if (runIdRef.current !== runId) return;

        updateSnapshot(prev => ({
          ...prev,
          activeProcessId: process.id,
        }));
      };

      for (const processId of graph.orderedProcessIds) {
        if (runIdRef.current !== runId) return;

        const process = graph.processes[processId];
        if (!process) continue;

        await ensureProcessStepVisible(cy, process, 'input');
        if (runIdRef.current !== runId) return;

        await animateTokens(runId, process.inputEdges, 'input', INPUT_ANIMATION_DURATION);
        if (runIdRef.current !== runId) return;

        updateSnapshot(prev => ({
          ...prev,
          activeProcessId: processId,
        }));

        await scheduleWait(PROCESS_SETTLE_DURATION);
        if (runIdRef.current !== runId) return;

        await executeChildDiagram(process, diagram);
        if (runIdRef.current !== runId) return;

        const inputs = process.inputEdges.reduce<Record<string, unknown>>((accumulator, edge) => {
          const sourceNode = graph.nodes[edge.sourceId];
          const sourceKey = sourceNode?.codeName || sourceNode?.label || edge.sourceId;
          const currentValue =
            currentRuntimeValues[edge.sourceId]?.rawValue ??
            getInitialNodeValue(sourceNode);
          accumulator[sourceKey] = currentValue;
          return accumulator;
        }, {});

        const result = await mockExecutionAdapter.executeProcess({
          process,
          graph,
          inputs,
          runtimeValues: currentRuntimeValues,
        });

        if (runIdRef.current !== runId) return;

        const nextRuntimeValues = { ...currentRuntimeValues };
        Object.entries(result.outputs).forEach(([nodeId, rawValue]) => {
          nextRuntimeValues[nodeId] = buildRuntimeValue(nodeId, rawValue, processId);
        });
        currentRuntimeValues = nextRuntimeValues;

        updateSnapshot(prev => ({
          ...prev,
          runtimeValues: currentRuntimeValues,
        }));

        await ensureProcessStepVisible(cy, process, 'output');
        if (runIdRef.current !== runId) return;

        await animateTokens(runId, process.outputEdges, 'output', OUTPUT_ANIMATION_DURATION);
        if (runIdRef.current !== runId) return;

        updateSnapshot(prev => ({
          ...prev,
          activeProcessId: null,
        }));
      }
    };

    updateSnapshot({
      status: 'running',
      activeProcessId: null,
      runtimeValues: {},
      tokens: [],
      startedAt: Date.now(),
    });

    try {
      if (startDiagram && navigateToDiagram) {
        await navigateToDiagram(startDiagram);
        if (runIdRef.current !== runId) return;
      }

      const startGraph = compileSimulationGraph(cy);
      if (startGraph.orderedProcessIds.length === 0) {
        throw new Error('当前图层没有可执行的过程节点');
      }

      await executeDiagram(startDiagram || null);
      if (runIdRef.current !== runId) return;

      if (runIdRef.current === runId) {
        updateSnapshot(prev => ({
          ...prev,
          status: 'completed',
          activeProcessId: null,
          tokens: [],
          finishedAt: Date.now(),
        }));
      }
    } catch (error: any) {
      if (runIdRef.current !== runId) return;
      updateSnapshot(prev => ({
        ...prev,
        status: 'failed',
        activeProcessId: null,
        tokens: [],
        errorMessage: error?.message || '仿真执行失败',
        finishedAt: Date.now(),
      }));
      throw error;
    }
  }, [
    animateTokens,
    clearScheduledTasks,
    navigateToDiagram,
    scheduleWait,
    updateSnapshot,
  ]);

  useEffect(() => {
    if (resetKey === undefined) return;
    if (lastResetKeyRef.current === resetKey) return;
    lastResetKeyRef.current = resetKey;
    resetSimulation();
  }, [resetKey, resetSimulation]);

  useEffect(() => {
    return () => {
      clearScheduledTasks();
      runIdRef.current += 1;
    };
  }, [clearScheduledTasks]);

  return {
    simulationSnapshot: snapshot,
    simulationStatus: snapshot.status,
    startSimulation,
    stopSimulation,
    resetSimulation,
  };
};
